import cn from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import styles from './index.module.scss';

enum RADIUS_TYPE {
  'normal' = 'normal',
  'noBottomRadius' = 'noBottomRadius',
  'noTopRadius' = 'noTopRadius'
}
export interface MaterialDesignInputProps {
  width?: string;
  prefix?: any;
  suffix?: any;
  type?: string;
  label?: string;
  value?: string;
  autoFocus?: boolean;
  max?: string;
  onChange?: any;
  onBlur?: any;
  onPressEnter?: any;
  errType?: string;
  autoComplete?: string;
  radiusType?: keyof typeof RADIUS_TYPE;
  /**报错的状态 */
  hasError?: boolean;
  id?: string;
  className?: string;
  suffixClassName?: string;
  maxLength?: number;
  onFocus?: any;
  placeholder?: string;
}

export default function MaterialDesignInput(props: MaterialDesignInputProps) {
  const { hasError = false, id = '', className, suffixClassName, placeholder } = props;
  const [isFocus, setIsFocus] = useState(false);
  const inputEl = useRef(null);
  const errTypeTxt = useMemo(() => (props.errType ? `-${props.errType}` : ''), []);
  const labelRef = useRef(null);

  useEffect(() => {
    if (props.value) {
      const _spanShrinked = labelRef.current?.querySelector('.material-label');
      if (_spanShrinked) {
        _spanShrinked?.classList.add(styles.labelShrinked);
      }
    }
  }, [props.value]);

  const radiusType = {
    [RADIUS_TYPE.noBottomRadius]: styles.noBottomRadius,
    [RADIUS_TYPE.noTopRadius]: styles.noTopRadius
  }[props?.radiusType];

  const outerStyles = twMerge(
    cn(styles.MDInput, radiusType || styles.rounded10, {
      [styles.MDInputFocus]: isFocus,
      [styles.MDInputFocusHasError]: hasError
    }),
    className
  );

  const labelStyles = cn(styles.label, 'material-label', {
    [styles.labelShrinkedWithFocus]: isFocus,
    [styles.labelShrinked]: isFocus || !!props.value,
    [styles.labelHasError]: hasError
  });

  const fieldStyles = cn(
    styles.field,
    {
      [styles.roundedNone]: !!props.prefix && !!props.suffix,
      [styles.roundedLnone]: !!props.prefix,
      [styles.roundedRnone]: !!props.suffix,
      [styles.roundedBnone]: props?.radiusType === RADIUS_TYPE.noBottomRadius,
      [styles.roundedTnone]: props?.radiusType === RADIUS_TYPE.noTopRadius,
      [styles.innerFocused]: isFocus,
      [styles.hasError]: hasError
    },
    className
  );

  const prefixWrapperStyles = cn(styles.prefixWrapper, radiusType || styles.rounded10, styles.roundedRnone, {
    [styles.innerFocused]: isFocus,
    [styles.prefixHasError]: hasError
  });

  const suffixWrapperStyles = cn(
    styles.suffixWrapper,
    radiusType || styles.rounded10,
    styles.roundedLnone,
    {
      [styles.innerFocused]: isFocus,
      [styles.suffixHasError]: hasError
    },
    suffixClassName
  );

  function handleChange(e) {
    props.onChange(e.target.value);
  }

  function handleKeyDown(e) {
    e.keyCode === 13 && props?.onPressEnter?.();
  }

  const handleBlur = e => {
    props.onBlur?.(e.target.value);
    setIsFocus(false);
  };

  const handleFocus = e => {
    setIsFocus(true);
    props.onFocus?.(e.target.value);
  };

  useEffect(() => {
    props.autoFocus && inputEl.current.focus();
  }, [props.autoFocus]);

  return (
    <label
      data-testid={`wid-${id}`}
      ref={labelRef}
      htmlFor={id}
      style={{ width: props?.width || '100%' }}
      className={outerStyles}
      data-role={`materal-design-input${errTypeTxt}`}>
      {!!props.prefix && (
        <div className={prefixWrapperStyles} data-role={`materal-design-input-prefix`}>
          {props.prefix}
        </div>
      )}

      <div className={styles.fieldWrapper}>
        <span data-testid={`wid-${id}-label`} className={twMerge(labelStyles)}>
          {props.label}
        </span>
        <input
          data-testid={`wid-${id}-input`}
          ref={inputEl}
          id={id}
          value={props.value || ''}
          className={fieldStyles}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          type={props?.type}
          max={props?.max}
          autoComplete={props?.autoComplete}
          maxLength={props.maxLength}
          spellCheck={false}
          placeholder={placeholder}
        />
      </div>
      {!!props.suffix && (
        <div data-testid={`wid-${id}-suffix`} className={suffixWrapperStyles} data-role={`materal-design-input-suffix`}>
          {props.suffix}
        </div>
      )}
    </label>
  );
}
